/**
 * CreatorEarningsTables — three per-creator earnings SUMMARY CARDS on the
 * Creators page (owner spec 2026-07-02). Clicking a card reveals its full list
 * (table) below:
 *   1. Earn through calling    — creators with >= 1 completed call
 *   2. Earn through brand deals — creators with >= 1 brand deal
 *   3. Earn through referral    — creators who referred >= 1 fan or creator
 *
 * Data: GET /dashboard/creators/earnings → { calling, brand, referral, note }.
 * Real names on screen (CEO-only); CSV export masks the name to creator_<id>.
 */
import React from "react";
import { tokensV2 as T } from "./tokens.v2";
import { formatINR, formatNum } from "./useDashboardPage.v2";

const TOP = 50;
const nameCsv = (r) => `creator_${r.id}`; // mask real name in exports

// ── CSV helpers ────────────────────────────────────────────────
function toCSV(rows, columns) {
  const esc = (v) => {
    v = v == null ? "" : String(v);
    return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  };
  const head = columns.map((c) => c.label).join(",");
  const body = rows
    .map((r) => columns.map((c) => esc(c.csv ? c.csv(r) : r[c.key])).join(","))
    .join("\n");
  return head + "\n" + body;
}
function downloadCSV(filename, rows, columns) {
  const blob = new Blob([toCSV(rows, columns)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Summary card (click to open the list) ──────────────────────
function EarnCard({ title, hint, total, count, accent, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: "left", cursor: "pointer", width: "100%",
        background: selected ? T.surface.cardHover : T.surface.card,
        border: `1px solid ${selected ? accent : T.border.default}`,
        borderLeft: `3px solid ${accent}`,
        boxShadow: selected ? `0 0 0 1px ${accent}` : "none",
        borderRadius: T.radius.lg, padding: T.space.xl,
        display: "flex", flexDirection: "column", gap: 5,
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: T.text.muted }}>{title}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: T.text.primary, fontVariantNumeric: "tabular-nums" }}>{formatINR(total)}</div>
      <div style={{ fontSize: 12, color: T.text.secondary }}>{formatNum(count)} creators · {hint}</div>
      <div style={{ fontSize: 12, fontWeight: 700, color: accent, marginTop: 2 }}>{selected ? "Hide list ▲" : "View list ▼"}</div>
    </button>
  );
}

// ── The list (table) shown when a card is open ─────────────────
function EarningsList({ section }) {
  const { title, subtitle, accent, rows, columns, csvName } = section;
  const shown = rows.slice(0, TOP);
  const th = {
    textAlign: "left", padding: "8px 12px", fontSize: 11, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.4, color: T.text.muted,
    borderBottom: `1px solid ${T.border.default}`, whiteSpace: "nowrap",
  };
  const td = {
    padding: "9px 12px", fontSize: 13, color: T.text.secondary,
    borderBottom: `1px solid ${T.border.subtle}`, whiteSpace: "nowrap",
  };
  return (
    <div style={{
      marginTop: 4, background: T.surface.card,
      border: `1px solid ${T.border.default}`, borderTop: `3px solid ${accent}`,
      borderRadius: T.radius.lg, padding: T.space.xl,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: T.text.primary }}>{title}</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 3 }}>
            {subtitle} · {rows.length > TOP ? `showing top ${TOP} of ${formatNum(rows.length)}` : `${formatNum(rows.length)} creators`}
          </div>
        </div>
        {rows.length > 0 && (
          <button
            type="button"
            onClick={() => downloadCSV(csvName, rows, columns)}
            style={{
              flexShrink: 0, background: T.surface.card, border: `1px solid ${T.border.default}`,
              borderRadius: 8, padding: "6px 12px", fontSize: 12, fontWeight: 700,
              color: T.text.primary, cursor: "pointer",
            }}
          >
            Export CSV ({formatNum(rows.length)})
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 13, color: T.text.muted, marginTop: 14 }}>No creators match this yet.</div>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 12 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
            <thead>
              <tr>
                <th style={{ ...th, width: 40 }}>#</th>
                {columns.map((c) => (
                  <th key={c.key} style={{ ...th, textAlign: c.num ? "right" : "left" }}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r, i) => (
                <tr key={r.id}>
                  <td style={{ ...td, color: T.text.muted }}>{i + 1}</td>
                  {columns.map((c) => (
                    <td key={c.key} style={{
                      ...td, textAlign: c.num ? "right" : "left",
                      fontWeight: c.strong ? 700 : 400,
                      color: c.strong ? T.text.primary : T.text.secondary,
                      fontVariantNumeric: c.num ? "tabular-nums" : "normal",
                    }}>
                      {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const sum = (rows, ...keys) => rows.reduce((s, r) => s + keys.reduce((a, k) => a + (Number(r[k]) || 0), 0), 0);

export function CreatorEarningsTables({ data }) {
  const [open, setOpen] = React.useState(null);
  if (!data) return null;
  const calling = data.calling || [];
  const brand = data.brand || [];
  const referral = data.referral || [];
  const withdrawCalling = data.withdrawCalling || [];
  const withdrawBrand = data.withdrawBrand || [];

  const SECTIONS = [
    {
      key: "calling", title: "Earn through calling", hint: "1+ completed call", accent: T.chart.orange,
      rows: calling, total: sum(calling, "call_earnings"),
      subtitle: "Creators with 1+ completed call, by call earnings", csvName: "creators-earn-calling.csv",
      columns: [
        { key: "name",          label: "Creator", csv: nameCsv },
        { key: "calls",         label: "Calls",         num: true, fmt: formatNum },
        { key: "call_earnings", label: "Call earnings", num: true, strong: true, fmt: formatINR },
      ],
    },
    {
      key: "brand", title: "Earn through brand deals", hint: "1+ brand deal", accent: T.chart.violet,
      rows: brand, total: sum(brand, "brand_earnings"),
      subtitle: "Creators with 1+ brand deal, by brand earnings", csvName: "creators-earn-brand.csv",
      columns: [
        { key: "name",           label: "Creator", csv: nameCsv },
        { key: "brand_deals",    label: "Brand deals",    num: true, fmt: formatNum },
        { key: "brand_earnings", label: "Brand earnings", num: true, strong: true, fmt: formatINR },
      ],
    },
    {
      key: "referral", title: "Earn through referral", hint: "referred 1+ fan / creator", accent: T.chart.teal,
      rows: referral, total: sum(referral, "fan_ref_earnings", "creator_ref_earnings"),
      subtitle: "Creators who referred 1+ fan or creator (₹50/fan · ₹750 refer + ₹250 join per creator)", csvName: "creators-earn-referral.csv",
      columns: [
        { key: "name",                 label: "Creator", csv: nameCsv },
        { key: "fans_referred",        label: "Fans referred",     num: true, fmt: formatNum },
        { key: "creators_referred",    label: "Creators referred", num: true, fmt: formatNum },
        { key: "fan_ref_earnings",     label: "Fan-ref ₹",         num: true, fmt: formatINR },
        { key: "creator_ref_earnings", label: "Creator-ref ₹",     num: true, strong: true, fmt: formatINR },
      ],
    },
    {
      key: "wdCalling", title: "Withdrawals — calling", accent: T.chart.yellow,
      rows: withdrawCalling, total: sum(withdrawCalling, "amount"),
      hint: `${formatNum(sum(withdrawCalling, "withdrawals"))} withdrawals`,
      subtitle: "Completed payouts from the call-earnings pot", csvName: "creators-withdraw-calling.csv",
      columns: [
        { key: "name",        label: "Creator", csv: nameCsv },
        { key: "withdrawals", label: "# withdrawals",     num: true, fmt: formatNum },
        { key: "amount",      label: "Amount withdrawn",  num: true, strong: true, fmt: formatINR },
      ],
    },
    {
      key: "wdBrand", title: "Withdrawals — brand", accent: T.chart.pink,
      rows: withdrawBrand, total: sum(withdrawBrand, "amount"),
      hint: `${formatNum(sum(withdrawBrand, "withdrawals"))} withdrawals`,
      subtitle: "Completed payouts from the brand-earnings pot", csvName: "creators-withdraw-brand.csv",
      columns: [
        { key: "name",        label: "Creator", csv: nameCsv },
        { key: "withdrawals", label: "# withdrawals",     num: true, fmt: formatNum },
        { key: "amount",      label: "Amount withdrawn",  num: true, strong: true, fmt: formatINR },
      ],
    },
  ];
  const openSection = SECTIONS.find((s) => s.key === open);

  return (
    <div id="creator-earnings" style={{ scrollMarginTop: 80, marginTop: 24, marginBottom: 28 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary, marginBottom: 2 }}>Creator earnings breakdown</div>
      <div style={{ fontSize: 12, color: T.text.muted, marginBottom: 14 }}>
        By income source. Tap a card to see the creator list. {data.note}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
        {SECTIONS.map((s) => (
          <EarnCard
            key={s.key}
            title={s.title}
            hint={s.hint}
            total={s.total}
            count={s.rows.length}
            accent={s.accent}
            selected={open === s.key}
            onClick={() => setOpen(open === s.key ? null : s.key)}
          />
        ))}
      </div>

      {openSection && (
        <div style={{ marginTop: 12 }}>
          <EarningsList section={openSection} />
        </div>
      )}
    </div>
  );
}
